import { useState } from 'react';
import { Bot, Workflow, Smartphone, MessageSquare, X } from 'lucide-react';

var phoneNumber = "7372529843";

const services = [
    {
        icon: Bot,
        title: 'AI Employees',
        description: 'Deploy intelligent virtual workers that handle complex tasks, learn from data, and scale with your business needs.',
        gradient: 'from-blue-500 to-cyan-500',
        bgGradient: 'from-blue-500/10 to-cyan-500/10',
        modal: <>
            <h2 className="text-2xl font-bold text-white mb-4">Experience our AI Employee in Action</h2>
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 mb-4">
            <p>Call to experience <strong>Ava</strong>, our AI-powered virtual receptionist who demonstrates how a 24/7 automated assistant can answer questions and schedule consultations for your business.</p>
            <br />
            <div className="call-box">
                <h3>📞 <strong>Call Now:&nbsp;
                    <a className='underline text-blue-400' href={`tel:${phoneNumber}`}>{phoneNumber}</a>
                </strong></h3>
                <p className='text-sm'>(Live AI demo — takes about 1–2 minutes)</p>
            </div>
            </div>
            <div className="space-y-2 text-sm text-slate-400">
                <p>💡 Speak naturally — Ava understands context and tone.</p>
                <p>🔒 All calls are private and used only to demonstrate your AI experience.</p>
            </div>
        </>
    },
    {
        icon: MessageSquare,
        title: 'Smart Chatbots',
        description: 'Engage customers 24/7 with conversational AI that understands context, answers questions, and drives conversions.',
        gradient: 'from-emerald-500 to-teal-500',
        bgGradient: 'from-emerald-500/10 to-teal-500/10',
        modal: <>
        <h2 className="text-2xl font-bold text-white mb-4">Try Our Live Chatbot Demo</h2>
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 mb-4">
          <p className="text-slate-300 text-lg leading-relaxed mb-4">
            <strong className="text-white">Meet Ava</strong> – our AI chatbot receptionist who serves as both a live demo and NeuroStudio's virtual employee.
          </p>
          <p className="text-slate-300 mb-4">
            Click the <strong className="text-emerald-400">"Chat with Ava"</strong> button in the bottom-right corner of your screen to start a conversation.
          </p>
          <div className="flex items-start space-x-3 bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
            <span className="text-2xl">💡</span>
            <div>
              <p className="text-blue-300 font-semibold mb-1">Can't see the chat button?</p>
              <p className="text-slate-400 text-sm">Try refreshing the page – the widget should appear in the bottom-right corner.</p>
            </div>
          </div>
        </div>
        <div className="space-y-2 text-sm text-slate-400">
          <p>✨ Ask Ava about our services</p>
          <p>📅 Schedule a consultation</p>
          <p>💬 See how AI chatbots can transform your customer engagement</p>
        </div>
      </>
    },
    {
        icon: Workflow,
        title: 'Backend Automation',
        description: 'Streamline operations with intelligent automation that connects systems, processes data, and eliminates manual work.',
        gradient: 'from-violet-500 to-purple-500',
        bgGradient: 'from-violet-500/10 to-purple-500/10',
        modal: <p>Streamline operations with intelligent automation that connects systems, processes data, and eliminates manual work.</p>
    },
    {
        icon: Smartphone,
        title: 'Web & App Design',
        description: 'Launch beautiful, responsive digital experiences crafted with cutting-edge design principles and best practices.',
        gradient: 'from-orange-500 to-red-500',
        bgGradient: 'from-orange-500/10 to-red-500/10',
    },
];

export default function Services() {
    const [activeService, setActiveService] = useState(null);

    const closeModal = () => setActiveService(null);

   const handleServiceClick = (service) => {
    
    
    setActiveService(service);
};


    return (
        <section className="relative py-8 pt-14 px-6">
            <div className="max-w-7xl mx-auto">
                <div className="text-center mb-20">
                    <h2 className="text-5xl md:text-6xl font-bold text-white mb-6">
                        Everything You Need to
                        <br />
                        <span className="bg-gradient-to-r from-blue-400 to-emerald-400 bg-clip-text text-transparent">
                            Build the Future
                        </span>
                    </h2>
                    <p className="text-xl text-slate-400 max-w-2xl mx-auto">
                        Comprehensive AI solutions designed to transform how you work and accelerate your growth
                    </p>
                </div>

                <div id='services' className="grid md:grid-cols-2 gap-8">
                    {services.map((service, index) => (
                        <div
                            key={index}
                            className="group relative bg-gradient-to-br from-slate-900/50 to-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-3xl p-8 hover:border-slate-600/50 transition-all duration-500 hover:transform hover:scale-105"
                        >
                            <div className={`absolute inset-0 bg-gradient-to-br ${service.bgGradient} rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />

                            <div className="relative z-10">
                                <div className={`w-16 h-16 bg-gradient-to-br ${service.gradient} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                                    <service.icon className="w-8 h-8 text-white" />
                                </div>

                                <h3 className="text-2xl font-bold text-white mb-4">
                                    {service.title}
                                </h3>

                                <p className="text-slate-300 leading-relaxed text-lg">
                                    {service.description}
                                </p>

                                <button
                                    onClick={() => {
                                        if (["Web & App Design", "Backend Automation"].includes(service.title)) {
                                            document.getElementById("cta")?.scrollIntoView({ behavior: "smooth" });
                                        } else {
                                            handleServiceClick(service);
                                        }
                                    }}
                                    className="mt-6 flex items-center text-slate-400 group-hover:text-white transition-colors"
                                >
                                    {["Web & App Design", "Backend Automation"].includes(service.title) ? (
                                        <span className="font-semibold">Learn More</span>
                                    ) : (
                                        <span className="font-semibold">View Demo</span>
                                    )}

                                    <svg
                                        className="w-5 h-5 ml-2 group-hover:translate-x-2 transition-transform"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M17 8l4 4m0 0l-4 4m4-4H3"
                                        />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Popup Modal for other services */}
            {activeService && (
                <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[9999]">
                    <div className="bg-slate-900 border border-slate-700 rounded-3xl p-8 max-w-lg mx-auto text-center relative">
                        <button
                            onClick={closeModal}
                            className="absolute top-4 right-4 text-slate-400 hover:text-white"
                        >
                            <X className="w-6 h-6" />
                        </button>

                        <div className={`w-16 h-16 mx-auto bg-gradient-to-br ${activeService.gradient} rounded-2xl flex items-center justify-center mb-6`}>
                            <activeService.icon className="w-8 h-8 text-white" />
                        </div>

                        <div className='text-slate-300 text-lg leading-relaxed'>
                            {activeService.modal}
                        </div>

                        <button
                            onClick={closeModal}
                            className="bg-gradient-to-r from-blue-500 to-emerald-500 text-white px-6 py-2 rounded-xl font-semibold hover:opacity-90 transition mt-6"
                        >
                            Close
                        </button>
                    </div>
                </div>
            )}

        </section>
    );
}